import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';

import Filter from '@decisiv/ui-components/lib/components/Filter';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import SelectableOptionsList from '../../shared/SelectableOptionsList';
import {
  FilterPanel,
  FilterPanelDivider,
  FilterPanelTitle,
  FilterSearchRow,
} from './styles';
import useStagedSelection, { ALL_OPTION_ID } from '../../shared/useStagedSelection';

export { ALL_OPTION_ID };

/**
 * One filter in the users list's filter row (PD-4063).
 *
 * Every menu in the design shares this anatomy: a title, an optional
 * search-as-you-type input, a divider, the options, and a footer holding CLEAR
 * and APPLY. Only Accounts and Job Role get the search input; Permissions has a
 * shorter title block and none.
 *
 * Selection is multi-select and staged: checking boxes builds a draft, and
 * nothing reaches the caller until APPLY. That keeps one refetch per menu rather
 * than one per checkbox, and lets dismissing the menu leave the applied filter
 * untouched. The draft and the select-all row live in useStagedSelection, which
 * the wizard's Accounts field shares.
 *
 * Filter + Popover + OptionsList rather than a KeyDS composite: the Figma
 * "Action Filter" instance is Filter variant="action", and Menu crashes on the
 * React 16 portal pins (see ManagementLayout/UserMenu.jsx).
 */
function FilterMenu({
  allOption,
  label,
  onApply,
  onSearch,
  options,
  searchPlaceholder,
  searchable,
  selected,
  title,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Accounts are searched server side, so the options arriving are already
  // narrowed. Job Role holds its whole list, so it narrows here.
  const visibleOptions = onSearch
    ? options
    : options.filter((option) =>
        option.label.toLowerCase().includes(query.trim().toLowerCase()),
      );

  const { draft, handleChange, items, selectedIds } = useStagedSelection({
    allOption,
    isOpen,
    options: visibleOptions,
    selected,
  });

  const Chevron = isOpen ? ChevronUp : ChevronDown;

  const close = () => {
    setIsOpen(false);
    setQuery('');
  };

  const apply = (values) => {
    onApply(values);
    close();
  };

  return (
    <Popover
      hideOnClickOutside
      onRequestHide={close}
      placement="bottom-start"
      showArrow={false}
      visible={isOpen}
      target={
        <Filter
          variant="action"
          active={selected.length > 0}
          count={selected.length || undefined}
          endIcon={Chevron}
          text={selected.length ? title : label}
          onClick={() => setIsOpen((open) => !open)}
        />
      }
    >
      <FilterPanel>
        <FilterPanelTitle>
          <P weight="semibold">{title}</P>
          {searchable && (
            <FilterSearchRow>
              {/* "Search as you type" in the design, which is
                  searchOn="change". Note SearchInput documents a
                  debounceTimeout it never reads, so onSearch fires per
                  keystroke — the caller debounces. */}
              <SearchInput
                hideLabel
                label={`Search ${title}`}
                placeholder={searchPlaceholder}
                searchOn="change"
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onSearch={onSearch}
              />
            </FilterSearchRow>
          )}
        </FilterPanelTitle>
        <FilterPanelDivider />
        <SelectableOptionsList
          multiple
          items={items}
          // OptionsList defaults pointerEvents to "none", which leaves the rows
          // unclickable.
          pointerEvents="auto"
          selected={selectedIds}
          onChange={handleChange}
          // This logs "Invalid prop `actions`" whatever you pass it, including
          // the library's own documented shapes — its propType is declared
          // through react-desc's oneOfType, which mis-validates. The Footer
          // reads left/right at runtime and renders correctly.
          actions={{
            left: [
              {
                text: 'Clear',
                kind: 'secondary',
                size: 'small',
                onClick: () => apply([]),
              },
            ],
            right: [
              {
                text: 'Apply',
                size: 'small',
                onClick: () => apply(draft),
              },
            ],
          }}
        />
      </FilterPanel>
    </Popover>
  );
}

FilterMenu.propTypes = {
  /** Adds a leading "All" row that selects or clears every option at once. */
  allOption: PropTypes.bool,
  /** Placeholder shown while nothing is selected, e.g. "All Accounts". */
  label: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  /** Supply to search server side; omit to narrow the given options locally. */
  onSearch: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  searchPlaceholder: PropTypes.string,
  searchable: PropTypes.bool,
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  /** The menu's heading, and the chip's text once something is selected. */
  title: PropTypes.string.isRequired,
};

FilterMenu.defaultProps = {
  allOption: false,
  onSearch: undefined,
  options: [],
  searchPlaceholder: undefined,
  searchable: false,
  selected: [],
};

export default FilterMenu;
